import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import {
  IconLinkVertical,
  IconLinkVerticalProps,
  InfoCardVariants,
} from '@backstage/core-components';
import { useCallback } from 'react';
import { ScmIntegrationIcon } from '@backstage/integration-react';
import { alertApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { AboutContent } from '@backstage/plugin-catalog';
import {
  GcpLogsIcon,
  GcpTracesIcon,
  ArgoCDIcon,
  IncidentIoIcon,
} from '../icons/icons';
import { Grid, IconButton } from '@material-ui/core';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import CachedIcon from '@material-ui/icons/Cached';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_VIEW_URL,
  ANNOTATION_SOURCE_LOCATION,
} from '@backstage/catalog-model';

const SOURCE_URL_PREFIX = 'url:';

const getSourceUrl = (entity: Entity) => {
  const entityMetadataEditUrl =
    entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const viewMetadataUrl = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
  const sourceLocation =
    entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION];

  return sourceLocation?.startsWith(SOURCE_URL_PREFIX)
    ? sourceLocation.replace(SOURCE_URL_PREFIX, '')
    : (entityMetadataEditUrl ?? viewMetadataUrl);
};

const useStyles = makeStyles(theme => ({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)', // for pages without content header
    marginBottom: '10px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
  links: {
    margin: theme.spacing(1, 0),
  },
}));

export interface AboutCardProps {
  variant?: InfoCardVariants;
}

export interface LinkIconRowProps {
  links: IconLinkVerticalProps[];
}

function LinkIconRow(props: LinkIconRowProps) {
  const { links } = props;
  const classes = useStyles();
  return (
    <Grid container className={classes.links} spacing={2}>
      {links.map((link, index) => (
        <Grid key={link.label} item>
          <IconLinkVertical key={index + 1} {...link} />
        </Grid>
      ))}
    </Grid>
  );
}

export function AboutCard(props: AboutCardProps) {
  const { variant } = props;
  const classes = useStyles();
  const { entity } = useEntity();

  const links = createLinks();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);

  let cardClass = '';
  let cardContentClass = '';
  switch (variant) {
    case 'gridItem':
      cardClass = classes.gridItemCard;
      cardContentClass = classes.gridItemCardContent;
      break;
    case 'fullHeight':
      cardClass = classes.fullHeightCard;
      cardContentClass = classes.fullHeightCardContent;
      break;
  }

  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );
  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Entity refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  return (
    <Card className={cardClass}>
      <CardHeader
        title="About"
        subheader={<LinkIconRow links={links} />}
        action={
          canRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <AboutContent entity={entity} />
      </CardContent>
    </Card>
  );
}

const createLinks = (): IconLinkVerticalProps[] => {
  const { entity } = useEntity();

  const sourceUrl = getSourceUrl(entity);
  const sourceCodeLink: IconLinkVerticalProps = {
    label: 'Source',
    disabled: !sourceUrl,
    icon: <ScmIntegrationIcon type="github" />,
    href: sourceUrl,
  };

  const tracesUrl = entity.metadata.links?.find(
    l => l.type === 'gcp-trace-explorer',
  );
  const tracesLink: IconLinkVerticalProps = {
    label: 'Traces',
    disabled: !tracesUrl,
    icon: <GcpTracesIcon />,
    href: tracesUrl?.url,
  };

  const logsUrl = entity.metadata.links?.find(
    l => l.type === 'gcp-stackdriver',
  );
  const logsLink: IconLinkVerticalProps = {
    label: 'logs',
    disabled: !logsUrl,
    icon: <GcpLogsIcon />,
    href: logsUrl?.url,
  };

  const alertsUrl = entity.metadata.links?.find(
    l => l.type === 'incident-io-alerts',
  );
  const alertsLink: IconLinkVerticalProps = {
    label: 'Alerts',
    disabled: !alertsUrl,
    icon: <IncidentIoIcon />,
    href: alertsUrl?.url,
  };

  const argoCDUrl = entity.metadata.links?.find(l => l.type === 'argocd');
  const argoCDLink: IconLinkVerticalProps = {
    label: 'argocd',
    disabled: !argoCDUrl,
    icon: <ArgoCDIcon />,
    href: argoCDUrl?.url,
  };

  return [sourceCodeLink, tracesLink, logsLink, alertsLink, argoCDLink];
};
